const dateTime = new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short' });
const rtf = new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' });

/** Absolute local time, or an en dash when the API reports null ("never"). */
export function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '–';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '–' : dateTime.format(d);
}

const UNITS: [Intl.RelativeTimeFormatUnit, number][] = [
  ['day', 86_400],
  ['hour', 3_600],
  ['minute', 60],
];

/** "5 minutes ago" style text; falls back to seconds for very recent timestamps. */
export function formatRelative(iso: string | null | undefined, now: number = Date.now()): string {
  if (!iso) return 'Never';
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return '–';
  const diffSec = Math.round((t - now) / 1000);
  const abs = Math.abs(diffSec);
  for (const [unit, size] of UNITS) {
    if (abs >= size) return rtf.format(Math.trunc(diffSec / size), unit);
  }
  return rtf.format(diffSec, 'second');
}

/** "42 s ago", "11 min ago", "3 h ago", "2 d ago"; "never" for null. A future time reads "0 s ago". */
export function formatAgo(iso: string | null | undefined, now: number = Date.now()): string {
  if (!iso) return 'never';
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return '–';
  const s = Math.max(0, Math.floor((now - t) / 1000));
  if (s < 60) return `${s} s ago`;
  if (s < 3_600) return `${Math.floor(s / 60)} min ago`;
  if (s < 86_400) return `${Math.floor(s / 3_600)} h ago`;
  return `${Math.floor(s / 86_400)} d ago`;
}

/** One decimal below 10, whole numbers above: "4.2", "96", "482". */
const scaled = (v: number) => (v < 10 ? v.toFixed(1) : String(Math.round(v)));

const BYTE_UNITS = ['KB', 'MB', 'GB', 'TB'];

/** 1000-based: "0 B", "512 B", "340 KB", "1.2 MB", "4.2 GB"; an en dash for null. */
export function formatBytes(bytes: number | null | undefined): string {
  if (bytes === null || bytes === undefined || !Number.isFinite(bytes)) return '–';
  if (bytes < 1000) return `${Math.max(0, Math.round(bytes))} B`;
  let v = bytes / 1000;
  let i = 0;
  // Compare the rounded figure, so 999 950 B reads "1.0 MB" rather than "1000 KB".
  while (i < BYTE_UNITS.length - 1 && Number(scaled(v)) >= 1000) {
    v /= 1000;
    i++;
  }
  return `${scaled(v)} ${BYTE_UNITS[i]}`;
}

/** Average Mbit/s of `bytes` over one `stepSeconds` sample: bytes·8 / step / 1e6. */
export function toMbit(bytes: number, stepSeconds: number): number {
  return stepSeconds > 0 ? (bytes * 8) / stepSeconds / 1e6 : 0;
}

/** "4.2 Mbit/s", "12 Mbit/s"; "0 Mbit/s" for nothing, "<0.1 Mbit/s" for a trickle. */
export function formatMbit(mbit: number): string {
  if (!Number.isFinite(mbit) || mbit <= 0) return '0 Mbit/s';
  if (mbit < 0.1) return '<0.1 Mbit/s';
  return `${scaled(mbit)} Mbit/s`;
}

const timeOnly = new Intl.DateTimeFormat(undefined, { timeStyle: 'short' });

/** "today 11:04" for today, else the absolute date and time; an en dash for null. */
export function formatDayTime(iso: string | null | undefined, now: number = Date.now()): string {
  if (!iso) return '–';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '–';
  return d.toDateString() === new Date(now).toDateString() ? `today ${timeOnly.format(d)}` : dateTime.format(d);
}
